import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { parseEmailList } from "@shared/schemas/admin";
import type { AppRoleName } from "@shared/schemas/auth";
import { Button } from "@client/components/Button";
import { SelectField, TextAreaField, TextField } from "@client/components/Field";
import { useAddAllowListEntry } from "@client/hooks/useAdmin";
import { createAllowListEntry } from "@client/lib/adminApi";
import { describeError, fieldErrors } from "@client/lib/errorMessage";
import { queryKeys } from "@client/lib/queryClient";
import { ROLE_LABEL, ROLE_ORDER } from "@client/lib/roles";

interface BulkResult {
  email: string;
  error?: string;
}

/**
 * Adding people, one at a time or a team at once.
 *
 * The bulk path posts each address separately rather than sending the list —
 * which is the API docs/06-api.md describes, and also the behaviour you want:
 * one bad address in a pasted list should add the other nine and tell you which
 * one it could not, not refuse the lot.
 */
export function AllowListAddForm() {
  const add = useAddAllowListEntry();
  const client = useQueryClient();

  const [bulk, setBulk] = useState(false);
  const [email, setEmail] = useState("");
  const [pasted, setPasted] = useState("");
  const [role, setRole] = useState<AppRoleName>("VIEWER");
  const [note, setNote] = useState("");

  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<BulkResult[] | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const candidates = parseEmailList(pasted);
  const emailError = fieldErrors(add.error).email;

  function onSubmitOne(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (add.isPending) return;
    setResults(null);
    add.mutate(
      { email: email.trim().toLowerCase(), role, note: note.trim() || null },
      {
        onSuccess: () => {
          setEmail("");
          setNote("");
        },
      },
    );
  }

  async function onSubmitMany(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (running || candidates.length === 0) return;

    setRunning(true);
    setBulkError(null);
    setResults(null);

    const collected: BulkResult[] = [];
    for (const candidate of candidates) {
      try {
        await createAllowListEntry({ email: candidate, role, note: note.trim() || null });
        collected.push({ email: candidate });
      } catch (error) {
        collected.push({ email: candidate, error: describeError(error) });
      }
    }

    // One invalidation for the whole paste, not one per address: twenty refetches
    // of the same table would be twenty chances to see it half-updated.
    await client.invalidateQueries({ queryKey: queryKeys.admin.all });
    setResults(collected);
    setRunning(false);
    const failed = collected.filter((result) => result.error).length;
    if (failed === collected.length && failed > 0) setBulkError("None of those could be added.");
    if (failed === 0) {
      setPasted("");
      setNote("");
    }
  }

  const roleSelect = (
    <SelectField
      label="Role"
      value={role}
      hint={role === "ADMIN" ? "Administrators can change this list." : undefined}
      onChange={(event) => setRole(event.target.value as AppRoleName)}
    >
      {ROLE_ORDER.map((option) => (
        <option key={option} value={option}>
          {ROLE_LABEL[option]}
        </option>
      ))}
    </SelectField>
  );

  const noteField = (
    <TextField
      label="Note"
      placeholder="Their team, or who asked for them"
      value={note}
      onChange={(event) => setNote(event.target.value)}
    />
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <Button
          variant="quiet"
          size="sm"
          onClick={() => {
            setBulk(!bulk);
            setResults(null);
            setBulkError(null);
          }}
        >
          {bulk ? "Add one address" : "Paste a list"}
        </Button>
      </div>

      {bulk ? (
        <form className="flex flex-col gap-3" onSubmit={(event) => void onSubmitMany(event)}>
          <TextAreaField
            label="Email addresses"
            rows={4}
            placeholder={"ada@dss.mv\ngrace@dss.mv"}
            hint={
              candidates.length > 0
                ? `${candidates.length} address${candidates.length === 1 ? "" : "es"}, separated by new lines, commas or spaces. They all get the role below.`
                : "One per line, or separated by commas. They all get the role below."
            }
            value={pasted}
            onChange={(event) => setPasted(event.target.value)}
          />
          <div className="grid gap-3 sm:grid-cols-[12rem_1fr_auto] sm:items-end">
            {roleSelect}
            {noteField}
            <Button type="submit" disabled={running || candidates.length === 0}>
              {running ? "Adding…" : `Add ${candidates.length || ""}`.trim()}
            </Button>
          </div>
        </form>
      ) : (
        <form
          className="grid gap-3 sm:grid-cols-[1fr_12rem_1fr_auto] sm:items-end"
          onSubmit={onSubmitOne}
        >
          <TextField
            label="Email address"
            type="email"
            inputMode="email"
            autoComplete="off"
            placeholder="someone@dss.mv"
            value={email}
            error={emailError}
            onChange={(event) => setEmail(event.target.value)}
          />
          {roleSelect}
          {noteField}
          <Button type="submit" disabled={add.isPending || email.trim().length === 0}>
            {add.isPending ? "Adding…" : "Add"}
          </Button>
        </form>
      )}

      {add.isError && !emailError && (
        <p role="alert" className="text-xs font-semibold" style={{ color: "var(--chart-crit)" }}>
          {describeError(add.error)}
        </p>
      )}

      {bulkError && (
        <p role="alert" className="text-xs font-semibold" style={{ color: "var(--chart-crit)" }}>
          {bulkError}
        </p>
      )}

      {results && (
        <div role="status" className="flex flex-col gap-1 rounded-lg border border-line px-3 py-2">
          <span className="text-xs font-semibold text-ink">
            {results.filter((result) => !result.error).length} of {results.length} added
          </span>
          {results
            .filter((result) => result.error)
            .map((result) => (
              <span key={result.email} className="text-[11px] text-ink-soft">
                <strong className="font-semibold text-ink">{result.email}</strong> — {result.error}
              </span>
            ))}
        </div>
      )}
    </div>
  );
}
